import { useEffect, useMemo, useRef, useState } from "react";
import { buildMailFrameSrcDoc, repairUnreadableMailColors } from "@/lib/mailDocument";

/**
 * HTML-Mails in einem eigenen Dokument anzeigen.
 * So bleiben Schrift und Hintergründe der Mail erhalten und werden nicht
 * vom dunklen App-Design oder von Dark-Mode-Regeln unsichtbar gemacht.
 */
export function MailMessageBody({
  html,
  text,
}: {
  html?: string | null;
  text?: string | null;
}) {
  const srcDoc = useMemo(() => buildMailFrameSrcDoc(html ?? ""), [html]);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(160);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !srcDoc) return;
    let observer: ResizeObserver | null = null;

    const fit = () => {
      const doc = frame.contentDocument;
      if (!doc) return;
      repairUnreadableMailColors(doc);
      const next = Math.max(doc.documentElement?.scrollHeight ?? 0, doc.body?.scrollHeight ?? 0);
      if (next > 0) {
        setHeight((current) => (Math.abs(current - next) > 2 ? next : current));
      }
      if (!observer && doc.body) {
        observer = new ResizeObserver(() => fit());
        observer.observe(doc.body);
      }
      doc.querySelectorAll("img").forEach((image) => {
        if (image.dataset.mailFit === "1") return;
        image.dataset.mailFit = "1";
        image.addEventListener("load", fit, { once: true });
      });
    };

    frame.addEventListener("load", fit);
    fit();
    const timers = [40, 200, 800].map((ms) => window.setTimeout(fit, ms));
    return () => {
      frame.removeEventListener("load", fit);
      observer?.disconnect();
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [srcDoc]);

  if (!srcDoc) {
    const plain = text?.trim() ?? "";
    if (!plain) {
      return <p className="mail-reading mail-reading-text">Diese Nachricht hat keinen lesbaren Inhalt.</p>;
    }
    return <pre className="mail-reading mail-reading-text">{plain}</pre>;
  }

  return (
    <div className="mail-frame">
      <iframe
        ref={frameRef}
        title="E-Mail-Inhalt"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
        className="mail-frame__view"
        style={{ height }}
      />
    </div>
  );
}
